import { useEffect, useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger
} from "@/components/ui/popover";
import {
  Tabs, TabsList, TabsTrigger, TabsContent
} from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  Plus, Trash2, CalendarIcon, Receipt as ReceiptIcon, FileDown, Fuel, Sparkles,
  AlertTriangle, Tag, Route, MessageCircle, Copy, ExternalLink, FileText, PenLine,
  ClipboardCheck, Camera, FileSignature, UserCheck, Star, Shield, DollarSign,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import Receipt from "@/components/Receipt";
import PhotoGallery from "@/components/PhotoGallery";
import SignaturePad from "@/components/SignaturePad";
import RentalChecklist from "@/components/RentalChecklist";
import AIInspector from "@/components/AIInspector";
import ReturnReport from "@/components/ReturnReport";
import PaymentStatusBadge from "@/components/PaymentStatusBadge";
const DATE_LOCALES = { pt: ptBR, en: enUS, es };

const CATEGORIES = ["ezpass", "fuel", "cleaning", "damage", "other"];
const CATEGORY_META = {
  ezpass:   { icon: Route,          bg: "bg-[#F5A623]/10 text-[#D97706] border-[#F5A623]/30" },
  fuel:     { icon: Fuel,           bg: "bg-red-50 text-red-700 border-red-200" },
  cleaning: { icon: Sparkles,       bg: "bg-blue-50 text-blue-700 border-blue-200" },
  damage:   { icon: AlertTriangle,  bg: "bg-rose-50 text-rose-800 border-rose-200" },
  other:    { icon: Tag,            bg: "bg-zinc-100 text-zinc-700 border-zinc-200" },
};

export default function RentalDetails({ open, onClose, rentalId, onChange }) {
  const { t, lang, fmtCurrency } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;
  const [rental, setRental] = useState(null);
  const [form, setForm] = useState({ date: new Date(), category: "ezpass", description: "", amount: "" });
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptType, setReceiptType] = useState("pickup");
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [checklist, setChecklist] = useState({ pickup: null, return: null });
  const [storedContracts, setStoredContracts] = useState([]);
  const [contractLang, setContractLang] = useState(lang);
  const aiInspectorRef = useRef(null);

  // Sync local toggle whenever the rental loads — prefer rental.contract_lang, fallback to UI lang
  useEffect(() => {
    if (rental?.contract_lang) setContractLang(rental.contract_lang);
  }, [rental?.contract_lang]);

  const changeContractLang = async (newLang) => {
    setContractLang(newLang);
    if (!rentalId) return;
    try {
      await api.put(`/rentals/${rentalId}/contract-lang`, { lang: newLang });
    } catch { /* ignore */ }
  };

  const generateAndStore = async () => {
    try {
      await api.post(`/rentals/${rentalId}/store-contract`, null, { params: { lang: contractLang } });
      toast.success(t("contract.generated", { lang: contractLang.toUpperCase() }));
      loadStoredContracts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const loadStoredContracts = async () => {
    if (!rentalId) return;
    try {
      const { data } = await api.get("/files", {
        params: { owner_type: "rental_contract", owner_id: rentalId },
      });
      setStoredContracts(data);
    } catch { /* ignore */ }
  };

  const loadRental = async () => {
    if (!rentalId) return;
    try {
      const { data } = await api.get(`/rentals/${rentalId}`);
      setRental(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadChecklist = async () => {
    if (!rentalId) return;
    try {
      const { data } = await api.get(`/rentals/${rentalId}/checklist`);
      setChecklist({ pickup: data.pickup, return: data.return });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (open && rentalId) {
      loadRental();
      loadChecklist();
      loadStoredContracts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rentalId]);

  if (!rental) return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-none">
        <DialogHeader><DialogTitle>{t("common.loading")}</DialogTitle></DialogHeader>
      </DialogContent>
    </Dialog>
  );

  const charges = rental.extra_charges || [];
  const chargesTotal = charges.reduce((a, c) => a + Number(c.amount || 0), 0);
  const deposit = Number(rental.deposit || 0);
  const rentalTotal = Number(rental.total || 0);
  const pickupTotal = rentalTotal + deposit;
  const refund = deposit - chargesTotal;

  const totalsByCategory = CATEGORIES.map((cat) => ({
    cat,
    total: charges.filter((c) => (c.category || "ezpass") === cat).reduce((a, c) => a + Number(c.amount || 0), 0),
  })).filter((x) => x.total > 0);

  const addCharge = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/rentals/${rentalId}/charges`, {
        date: new Date(form.date).toISOString(),
        category: form.category,
        description: form.description,
        amount: Number(form.amount),
      });
      setForm({ date: new Date(), category: form.category, description: "", amount: "" });
      toast.success(t("rentalDetails.ezpassAdded"));
      loadRental(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeCharge = async (id) => {
    try {
      await api.delete(`/rentals/${rentalId}/charges/${id}`);
      toast.success(t("rentalDetails.removed"));
      loadRental(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openReceipt = (type) => { setReceiptType(type); setReceiptOpen(true); };

  const generateBilling = async () => {
    setBillingLoading(true);
    setBilling(null);
    try {
      const { data } = await api.post("/billing/checkout-link", {
        rental_id: rentalId,
        origin_url: window.location.origin,
      });
      setBilling(data);
      if (data.whatsapp_sent) {
        toast.success(t("billing.sentAutomatically"));
      } else {
        toast.success(t("billing.linkGenerated"));
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBillingLoading(false); }
  };

  const generateZelleOnly = async () => {
    setBillingLoading(true);
    setBilling(null);
    try {
      const { data } = await api.post("/billing/zelle-only", {
        rental_id: rentalId,
      });
      setBilling({ ...data, zelle_only: true });
      if (data.whatsapp_sent) {
        toast.success(t("billing.zelleSentAutomatically"));
      } else {
        toast.success(t("billing.zelleMessageReady"));
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBillingLoading(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(billing.checkout_url);
      toast.success(t("billing.linkCopied"));
    } catch { /* ignore */ }
  };

  const removeSignature = async () => {
    if (!window.confirm(t("signature.confirmRemove"))) return;
    try {
      await api.delete(`/rentals/${rentalId}/signature`);
      toast.success(t("signature.removed"));
      loadRental();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const contractUrl = `${API}/rentals/${rentalId}/contract.pdf?lang=${contractLang}`;

  const sendCheckinLink = async () => {
    try {
      const { data } = await api.post(`/rentals/${rentalId}/self-checkin-token`, {
        origin_url: window.location.origin,
      });
      if (data.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank");
      } else {
        await navigator.clipboard.writeText(data.url);
        toast.success(t("rentalDetails.checkinLinkCopied"));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const requestReview = async () => {
    try {
      const { data } = await api.post(`/rentals/${rentalId}/review-link`);
      if (data.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank");
      } else if (data.mailto_url) {
        window.open(data.mailto_url, "_blank");
      } else {
        await navigator.clipboard.writeText(data.google_review_url);
        toast.success(t("rentalDetails.reviewLinkCopied"));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl rounded-none max-h-[94vh] overflow-y-auto" data-testid="rental-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {rental.car?.model} · {rental.car?.plate} — {rental.customer?.name}
            </DialogTitle>
            {rental.status === "ativo" && rental.payment_status && (
              <div className="mt-2">
                <PaymentStatusBadge paymentStatus={rental.payment_status} showAmount />
              </div>
            )}
          </DialogHeader>

          <div className="grid grid-cols-3 gap-0 border-l border-t border-zinc-200">
            <Tile label={t("receipt.rental")} value={fmtCurrency(rentalTotal)} />
            <Tile label={t("rentalDetails.depositLabel")} value={fmtCurrency(deposit)} />
            <Tile label={t("rentals.pickupTotal")} value={fmtCurrency(pickupTotal)} tone="primary" />
          </div>

          <div className="grid grid-cols-3 gap-0 border-l border-t border-zinc-200">
            <Tile label={t("rentalDetails.ezpassTotal")} value={fmtCurrency(chargesTotal)} tone="alert" />
            <Tile label={t("rentalDetails.depositMinus")} value={fmtCurrency(refund)} tone={refund < 0 ? "alert" : "success"} />
            <Tile label={t("common.status")} value={t(`rentals.status.${rental.status}`)} />
          </div>

          {/* Quick actions */}
          <div className="flex gap-2 flex-wrap pt-2 items-center">
            <Button onClick={() => openReceipt("pickup")} className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-white" data-testid="open-pickup-receipt-button">
              <ReceiptIcon className="w-4 h-4 mr-2" /> {t("rentalDetails.pickupReceiptBtn")}
            </Button>
            <Button onClick={() => openReceipt("return")} variant="outline" className="rounded-none border-[#F5A623] text-[#D97706] hover:bg-[#F5A623] hover:text-white" data-testid="open-return-receipt-button">
              <FileDown className="w-4 h-4 mr-2" /> {t("rentalDetails.returnReceiptBtn")}
            </Button>

            {/* Contract language toggle + Open PDF + Generate-store */}
            <div className="inline-flex items-stretch border border-zinc-300 bg-white" data-testid="contract-lang-toggle">
              {["pt", "en", "es"].map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => changeContractLang(code)}
                  className={`px-2.5 text-[10px] font-mono uppercase tracking-wider transition-colors border-r last:border-r-0 border-zinc-300 ${
                    contractLang === code
                      ? "bg-[#F5A623] text-white"
                      : "text-zinc-600 hover:bg-zinc-100"
                  }`}
                  data-testid={`contract-lang-${code}`}
                >
                  {code}
                </button>
              ))}
            </div>
            <a href={contractUrl} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 rounded-none bg-black hover:bg-zinc-800 text-white px-4 py-2 text-sm transition-colors"
               data-testid="open-contract-pdf">
              <FileText className="w-4 h-4" /> {t("contract.openPdf")}
            </a>
            <Button
              type="button"
              variant="outline"
              onClick={generateAndStore}
              className="rounded-none border-zinc-400 hover:border-[#F5A623] hover:text-[#D97706]"
              data-testid="generate-store-contract"
            >
              <FileText className="w-4 h-4 mr-2" /> {t("contract.storeInLang", { lang: contractLang.toUpperCase() })}
            </Button>

            <Button onClick={generateBilling} disabled={billingLoading} className="rounded-none bg-[#25D366] hover:bg-[#1FAE54] text-white" data-testid="generate-billing-button">
              <MessageCircle className="w-4 h-4 mr-2" /> {billingLoading ? t("common.loading") : t("billing.chargeViaWhatsapp")}
            </Button>
            <Button onClick={generateZelleOnly} disabled={billingLoading} className="rounded-none bg-[#7B3FF2] hover:bg-[#6D1FE0] text-white" data-testid="generate-zelle-only-button" title={t("billing.zelleOnlyHint")}>
              <DollarSign className="w-4 h-4 mr-2" /> {t("billing.zelleOnly")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={sendCheckinLink}
              className="rounded-none border-zinc-400 hover:border-[#F5A623] hover:text-[#D97706]"
              data-testid="send-checkin-link"
            >
              <UserCheck className="w-4 h-4 mr-2" /> {t("rentalDetails.sendCheckinLink")}
            </Button>
            {rental.status === "concluido" && (
              <Button
                type="button"
                onClick={requestReview}
                className="rounded-none bg-yellow-500 hover:bg-yellow-600 text-white"
                data-testid="request-review"
              >
                <Star className="w-4 h-4 mr-2" /> {t("rentalDetails.requestReview")}
              </Button>
            )}
          </div>

          {/* Billing result */}
          {billing && (
            <div className="border-2 border-[#25D366] bg-emerald-50/40 p-5 mt-2 space-y-4" data-testid="billing-result-panel">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="overline text-[#1FAE54]">{billing.zelle_only ? t("billing.zelleReadyToSend") : t("billing.readyToSend")}</div>
                  <div className="font-display font-black text-2xl tracking-tighter">{fmtCurrency(billing.amount)}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {billing.whatsapp_url ? (
                    <a href={billing.whatsapp_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FAE54] text-white px-4 py-2 text-sm transition-colors"
                       data-testid="open-whatsapp-link">
                      <MessageCircle className="w-4 h-4" /> {t("billing.openWhatsapp")}
                    </a>
                  ) : (
                    <div className="text-xs text-red-700">{t("billing.noPhone")}</div>
                  )}
                  {billing.checkout_url && (
                    <a href={billing.checkout_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-2 border border-zinc-300 hover:border-zinc-500 px-4 py-2 text-sm"
                       data-testid="open-checkout-link">
                      <ExternalLink className="w-4 h-4" /> {t("billing.previewLink")}
                    </a>
                  )}
                  {billing.checkout_url && (
                    <button onClick={copyLink}
                       className="inline-flex items-center gap-2 border border-zinc-300 hover:border-zinc-500 px-4 py-2 text-sm"
                       data-testid="copy-link-button">
                      <Copy className="w-4 h-4" /> {t("billing.copyLink")}
                    </button>
                  )}
                </div>
              </div>
              {billing.zelle_qr_file_id && billing.zelle_only && (
                <div className="flex items-center gap-3 bg-white border border-zinc-200 p-3">
                  <img src={`${API}/files/${billing.zelle_qr_file_id}/raw`} alt="Zelle QR" className="w-24 h-24 object-contain" data-testid="zelle-qr-in-billing" />
                  <div className="text-xs text-zinc-600">{t("billing.zelleQrShare")}</div>
                </div>
              )}
              <div>
                <div className="overline mb-1">{t("billing.previewMessage")}</div>
                <pre className="text-xs bg-white border border-zinc-200 p-3 whitespace-pre-wrap font-mono text-zinc-700 max-h-40 overflow-y-auto">{billing.whatsapp_message}</pre>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="charges" className="mt-4">
            <TabsList className="rounded-none w-full justify-start bg-zinc-100 p-0 h-auto flex-wrap">
              <TabsTrigger value="charges" className="rounded-none data-[state=active]:bg-[#F5A623] data-[state=active]:text-white text-xs py-2.5 px-4" data-testid="tab-charges">
                <Tag className="w-3.5 h-3.5 mr-1.5" /> {t("rentalDetails.tabCharges")}
              </TabsTrigger>
              <TabsTrigger value="photos" className="rounded-none data-[state=active]:bg-[#F5A623] data-[state=active]:text-white text-xs py-2.5 px-4" data-testid="tab-photos">
                <Camera className="w-3.5 h-3.5 mr-1.5" /> {t("rentalDetails.tabPhotos")}
              </TabsTrigger>
              <TabsTrigger value="checklist" className="rounded-none data-[state=active]:bg-[#F5A623] data-[state=active]:text-white text-xs py-2.5 px-4" data-testid="tab-checklist">
                <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> {t("rentalDetails.tabChecklist")}
              </TabsTrigger>
              <TabsTrigger value="signature" className="rounded-none data-[state=active]:bg-[#F5A623] data-[state=active]:text-white text-xs py-2.5 px-4" data-testid="tab-signature">
                <FileSignature className="w-3.5 h-3.5 mr-1.5" /> {t("rentalDetails.tabSignature")}
              </TabsTrigger>
              <TabsTrigger value="return-report" className="rounded-none data-[state=active]:bg-violet-600 data-[state=active]:text-white text-xs py-2.5 px-4" data-testid="tab-return-report">
                <Shield className="w-3.5 h-3.5 mr-1.5" /> {t("rentalDetails.tabReturnReport")}
              </TabsTrigger>
            </TabsList>

            {/* CHARGES */}
            <TabsContent value="charges" className="mt-4 space-y-4">
              {totalsByCategory.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-l border-t border-zinc-200">
                  {totalsByCategory.map(({ cat, total }) => {
                    const Meta = CATEGORY_META[cat];
                    const Icon = Meta.icon;
                    return (
                      <div key={cat} className="border-r border-b border-zinc-200 -ml-px -mt-px bg-white p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon className="w-3 h-3 text-zinc-500" />
                          <div className="overline">{t(`rentalDetails.categories.${cat}`)}</div>
                        </div>
                        <div className="font-mono font-semibold text-sm">{fmtCurrency(total)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="border border-zinc-200">
                <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
                  <div className="overline">{t("rentalDetails.ezpassSection")}</div>
                </div>

                <form onSubmit={addCharge} className="grid grid-cols-12 gap-2 p-4 border-b border-zinc-200 items-end">
                  <div className="col-span-12 md:col-span-2 space-y-1">
                    <label className="overline">{t("rentalDetails.category")}</label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="rounded-none h-9 text-xs" data-testid="charge-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>{t(`rentalDetails.categories.${c}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6 md:col-span-2 space-y-1">
                    <label className="overline">{t("common.date")}</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="rounded-none w-full justify-start font-normal text-xs h-9">
                          <CalendarIcon className="w-3 h-3 mr-2" />
                          {format(form.date, "dd/MM/yyyy")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-none">
                        <Calendar mode="single" selected={form.date} onSelect={(d) => d && setForm({ ...form, date: d })} locale={dateLocale} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="col-span-12 md:col-span-4 space-y-1">
                    <label className="overline">{t("common.description")}</label>
                    <Input className="rounded-none h-9" required placeholder={t(`rentalDetails.placeholders.${form.category}`)} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="charge-description-input" />
                  </div>
                  <div className="col-span-6 md:col-span-2 space-y-1">
                    <label className="overline">{t("payments.amount")}</label>
                    <Input type="number" step="0.01" className="rounded-none h-9" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="charge-amount-input" />
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Button type="submit" className="rounded-none w-full bg-black hover:bg-[#F5A623] text-white h-9 transition-colors" data-testid="add-charge-button">
                      <Plus className="w-4 h-4 mr-1" /> {t("rentalDetails.addToll")}
                    </Button>
                  </div>
                </form>

                {charges.length === 0 ? (
                  <div className="p-8 text-center text-sm text-zinc-500">{t("receipt.noTolls")}</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr className="text-left">
                        <th className="px-4 py-2 overline">{t("rentalDetails.category")}</th>
                        <th className="px-4 py-2 overline">{t("common.date")}</th>
                        <th className="px-4 py-2 overline">{t("common.description")}</th>
                        <th className="px-4 py-2 overline text-right">{t("common.value")}</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {charges.map((c) => {
                        const cat = c.category || "ezpass";
                        const Meta = CATEGORY_META[cat];
                        const Icon = Meta.icon;
                        return (
                          <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`charge-row-${c.id}`}>
                            <td className="px-4 py-2">
                              <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${Meta.bg}`}>
                                <Icon className="w-3 h-3" /> {t(`rentalDetails.categories.${cat}`)}
                              </span>
                            </td>
                            <td className="px-4 py-2 font-mono text-xs">{format(parseISO(c.date), "dd/MM/yyyy")}</td>
                            <td className="px-4 py-2">{c.description}</td>
                            <td className="px-4 py-2 text-right font-mono font-semibold">{fmtCurrency(c.amount)}</td>
                            <td className="px-4 py-2 text-right">
                              <button onClick={() => removeCharge(c.id)} className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-zinc-50 font-semibold">
                        <td className="px-4 py-2" colSpan={3}>{t("common.total")}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmtCurrency(chargesTotal)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </TabsContent>

            {/* PHOTOS */}
            <TabsContent value="photos" className="mt-4 space-y-6">
              <PhotoGallery
                ownerType="rental_pickup"
                ownerId={rentalId}
                title={t("photos.pickupCondition")}
                description={t("photos.pickupConditionHint")}
                gridCols={4}
              />
              <PhotoGallery
                ownerType="rental_return"
                ownerId={rentalId}
                title={t("photos.returnCondition")}
                description={t("photos.returnConditionHint")}
                gridCols={4}
                onUploaded={() => {
                  // Auto-trigger AI inspection the first time return photos are uploaded
                  setTimeout(() => aiInspectorRef.current?.triggerAuto?.(), 600);
                }}
              />
              <AIInspector
                ref={aiInspectorRef}
                rentalId={rentalId}
                onChargeApplied={() => loadRental?.()}
              />
            </TabsContent>

            {/* CHECKLIST */}
            <TabsContent value="checklist" className="mt-4 space-y-4">
              <RentalChecklist rentalId={rentalId} side="pickup" existing={checklist.pickup} onSaved={loadChecklist} />
              <RentalChecklist rentalId={rentalId} side="return" existing={checklist.return} onSaved={loadChecklist} />
            </TabsContent>

            {/* SIGNATURE */}
            <TabsContent value="signature" className="mt-4 space-y-3">
              <div className="bg-orange-50 border border-[#F5A623]/30 px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-zinc-700">
                  <span className="font-semibold">{t("signature.langBannerTitle")}: </span>
                  {t("signature.langBannerHint")}
                </div>
                <div className="inline-flex items-stretch border border-zinc-300 bg-white" data-testid="signature-lang-toggle">
                  {["pt", "en", "es"].map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => changeContractLang(code)}
                      className={`px-3 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors border-r last:border-r-0 border-zinc-300 ${
                        contractLang === code ? "bg-[#F5A623] text-white" : "text-zinc-600 hover:bg-zinc-100"
                      }`}
                      data-testid={`signature-lang-${code}`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
              <SignaturePad
                rentalId={rentalId}
                existing={rental.customer_signature}
                existingInitials={rental.customer_initials_text}
                onSaved={() => { loadRental(); loadStoredContracts(); }}
              />
              {rental.customer_signature && (
                <div className="flex items-center justify-between border border-zinc-200 bg-zinc-50 px-4 py-3">
                  <div className="text-xs">
                    <div className="overline">{t("signature.signedOn")}</div>
                    <div className="font-mono text-zinc-700">
                      {rental.signed_at ? new Date(rental.signed_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <button
                    onClick={removeSignature}
                    className="text-xs border border-zinc-300 hover:border-red-500 hover:text-red-600 px-3 py-1.5"
                    data-testid="signature-remove"
                  >
                    <Trash2 className="w-3 h-3 inline mr-1" /> {t("signature.remove")}
                  </button>
                </div>
              )}
              {storedContracts.length > 0 && (
                <div className="border-2 border-[#F5A623] bg-orange-50/40 p-4" data-testid="stored-contracts-panel">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="overline text-[#D97706] flex items-center gap-1.5">
                        <FileText className="w-3 h-3" /> {t("signature.contractStored")}
                      </div>
                      <p className="text-xs text-zinc-600 mt-1">{t("signature.contractStoredHint")}</p>
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">
                      {storedContracts.length} {t("signature.versionsSaved")}
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {storedContracts.slice(0, 3).map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-2 bg-white border border-zinc-200 px-3 py-2 text-xs">
                        <div className="min-w-0">
                          <div className="font-mono font-semibold truncate">{c.original_filename}</div>
                          <div className="text-[10px] text-zinc-500">
                            {new Date(c.created_at).toLocaleString()} · {(c.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            const res = await api.get(`/files/${c.id}/raw`, { responseType: "blob" });
                            const url = URL.createObjectURL(res.data);
                            window.open(url, "_blank");
                            setTimeout(() => URL.revokeObjectURL(url), 60_000);
                          }}
                          className="border border-zinc-300 hover:border-[#F5A623] hover:text-[#D97706] px-2 py-1 inline-flex items-center gap-1"
                          data-testid={`stored-contract-open-${c.id}`}
                        >
                          <FileText className="w-3 h-3" /> {t("contracts.view")}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-zinc-500 border border-dashed border-zinc-300 p-3 bg-zinc-50/50">
                <PenLine className="w-3 h-3 inline mr-1 text-[#F5A623]" />
                {t("signature.contractHint")}
              </div>
            </TabsContent>

            {/* RETURN REPORT */}
            <TabsContent value="return-report" className="mt-4">
              {rental && <ReturnReport rental={rental} onSigned={loadRental} />}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Receipt open={receiptOpen} onClose={() => setReceiptOpen(false)} rental={rental} type={receiptType} />
    </>
  );
}

function Tile({ label, value, tone = "default" }) {
  const tones = {
    default: "text-zinc-900",
    primary: "text-[#F5A623]",
    success: "text-green-700",
    alert: "text-red-700",
  };
  return (
    <div className="border-r border-b border-zinc-200 -ml-px -mt-px p-4 bg-white">
      <div className="overline mb-2">{label}</div>
      <div className={`font-display font-black text-2xl tracking-tighter ${tones[tone]}`}>{value}</div>
    </div>
  );
}
